import { ReportDbtMetadataFacade, ReportViewerFacade } from '@mode/report-viewer/domain';
import { ReportTypes, UserResourceTypes } from '@mode/shared/contract-common';
import * as angular from 'angular';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { ReportResource, ReportResourceHelper } from '@mode/legacy/data-access-model-js';
import { ReportRunState } from '@mode/legacy/types-app-state';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { MixableBaseController } from '~/common/component';
import { AppStore } from '~/types/main.types';

import { DatasourceFacade } from '@mode/accounts/domain';
import { DatasourceDomain } from '@mode/shared/contract-common';
import { DataViewFacade } from '@mode/switch-views/contract';
import { isPresent } from '@mode/shared/util-js';
import { RouterStoreService } from '~/services/router-store-service';
import { ReportViewDataService } from '../../services';

class ReportViewDetailsController extends MixableBaseController implements angular.IOnInit {
  static $inject = [
    'ReportViewDataService',
    'RxStoreService',
    '$stateParams',
    'ReportViewerFacade',
    'RouterStoreService',
    'DatasourceFacade',
    'DataViewFacade',
    'FeatureFlagsFacade',
    'ReportDbtMetadataFacade',
  ];

  report$: Observable<any>;
  reportRun$: Observable<any>;
  windowReport: any;
  runIdUrlParam: string;
  currentRunToken$: Observable<string>;
  reportRunsCollection$: Observable<any>;
  referenceData$: Observable<any[]>;
  contextAccount$: Observable<any>;
  account$: Observable<any>;
  schedulesCollection$: Observable<any>;
  creator$: Observable<UserResourceTypes.UserResource>;
  hasPublished$: Observable<boolean>;
  canEditReport$: Observable<boolean>;
  canSeeHistory$: Observable<boolean>;
  createdAt$: Observable<ReportResource['created_at']>;
  description$: Observable<ReportResource['description']>;
  displayName$: Observable<string>;
  lastSuccessfullyRunAt$: Observable<ReportRunState['createdAt']>;
  parentReportName$: Observable<string>;
  parentReportLink$: Observable<string>;
  reportDbtData$: Observable<ReportTypes.ReportDbtMetaData>;
  reportDbtDataLoading$: Observable<boolean>;
  // exact dbt properties for dbt-details-table
  dbtTables: Array<ReportTypes.DbtDetailTable>;
  dbtSourceLastLoadTime: string;
  dbtStatusMessage: string;
  dbtIntegrated: boolean;
  dbtStatus: ReportTypes.dbtStateStatus;
  dbtMessage: string;

  constructor(
    private reportViewDataService: ReportViewDataService,
    private rxStore: AppStore,
    private $stateParams,
    private reportViewerFacade: ReportViewerFacade,
    private routerStoreService: RouterStoreService,
    private datasourceFacade: DatasourceFacade,
    private dataViewFacade: DataViewFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private dbtMetadataFacade: ReportDbtMetadataFacade
  ) {
    super();
  }

  $onInit(): void {
    this.runIdUrlParam = this.$stateParams['runId'];

    // Get legacy resources from `reportViewDataService`
    // NOTE - These are used only to power the legacy schedules and history components. No other data
    // should be picked from here. At some point in the future these components will be refactored, and these
    // dependencies will be able to be removed completely.
    this.report$ = this.reportViewDataService.report$;
    this.contextAccount$ = this.reportViewDataService.contextAccount$;
    this.account$ = this.reportViewDataService.account$;
    this.schedulesCollection$ = this.reportViewDataService.schedulesCollection$;
    this.reportRun$ = this.reportViewDataService.reportRun$;
    this.reportRunsCollection$ = this.reportViewDataService.reportRunsCollection$;
    this.currentRunToken$ = this.reportViewDataService.runToken$;

    this.creator$ = this.reportViewerFacade.creator$;
    this.hasPublished$ = this.reportViewerFacade.hasPublished$;
    this.canSeeHistory$ = this.reportViewerFacade.canSeeHistory$;
    this.parentReportName$ = this.reportViewerFacade.parentReportName$;
    this.parentReportLink$ = this.reportViewerFacade.parentReportLink$;
    this.createdAt$ = this.reportViewerFacade.createdAt$;
    this.description$ = this.reportViewerFacade.description$;
    this.displayName$ = this.reportViewerFacade.displayName$;
    this.lastSuccessfullyRunAt$ = this.reportViewerFacade.lastSuccessfullyRunAt$;
    this.reportDbtData$ = this.dbtMetadataFacade.reportDbtMetadata$;
    this.reportDbtDataLoading$ = this.dbtMetadataFacade.reportDbtMetadataLoading$;
    this.reportDbtData$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((reportDbtData) => this.retrieveDbtProperties(reportDbtData));

    this.getReferenceData();

    this.canEditReport$ = this.report$.pipe(
      map((report) => !!ReportResourceHelper.canEditReport(report && report._resource))
    );

    this.report$.pipe(filter(isPresent), takeUntil(this.destroyed$)).subscribe(() => this.loadReportDetailResources());
  }

  loadReportDetailResources() {
    this.reportViewDataService.getReportRunsCollection();
    this.reportViewDataService.getSchedulesCollection();
  }

  retrieveDbtProperties(reportDbtData: ReportTypes.ReportDbtMetaData) {
    if (reportDbtData) {
      this.dbtSourceLastLoadTime = reportDbtData.sourceDataLastLoadedAt;
      this.dbtTables = reportDbtData.tables;
      this.dbtStatus = reportDbtData.state;
      this.dbtMessage = reportDbtData.message;
    }
  }

  getNavLink(token: string) {
    const origin = window.location.origin;
    return origin + this.routerStoreService.datasetResultUrl(token);
  }

  getDataSourceName(datasource: DatasourceDomain.Datasource) {
    return (
      datasource && {
        href: datasource.links.settings,
        text: datasource.displayName,
        type: 'datasource',
      }
    );
  }

  getQueryName(results: any) {
    return (
      results && {
        text: results.displayName,
        href: this.getNavLink(results.token),
      }
    );
  }

  getReferenceData() {
    this.referenceData$ = combineLatest([
      this.dataViewFacade.enhancedDatasetResults$,
      this.contextAccount$,
      this.datasourceFacade.reportDatasources$,
    ]).pipe(
      map(([queryDatasetResults, contextAccountModel, datasources]) => {
        if (!contextAccountModel) {
          return [];
        }

        return queryDatasetResults.map((datasetResult) => {
          const datasource = datasources.find((ds) => ds.id === (datasetResult.datasourceId as any)); // Bad type info on data_source_id
          return {
            default_access: datasource?.defaultAccessLevel ?? null,
            name: this.getDataSourceName(datasource) ?? null,
            query: this.getQueryName(datasetResult) || null,
          };
        });
      })
    );
  }
}

export const ID = 'reportDetails';

export const reportDetails: angular.IComponentOptions = {
  template: `
  <div class="report-details">
    <div class="report-details-tab">
      <div class="report-details-body-wrap">
        <div class="report-details-body layout-content">
          <div class="next-well">
            <report-details-title
              has-published="ctrl.hasPublished$ | async:this"
              parent-report-name="ctrl.parentReportName$ | async:this"
              parent-report-link="ctrl.parentReportLink$ | async:this"
              created-at="ctrl.createdAt$ | async:this"
              description="ctrl.description$ | async:this"
              display-name="ctrl.displayName$ | async:this"
              last-run-at="ctrl.lastSuccessfullyRunAt$ | async:this"
              creator="ctrl.creator$ | async:this"
            ></report-details-title>
          </div>

          <div class="next-well" ng-if="(ctrl.canEditReport$ | async:this)">
            <div class="report-detail-title referenced-data-title">
              <span>Referenced data</span>
            </div>
            <div ng-if="(ctrl.referenceData$ | async:this)">
              <span class="referenced-data-desc">
                This table displays all Connections that are referenced by any query, as of the Report’s most recent run.
              </span>
              <span ng-show="!(ctrl.referenceData$ | async:this).length"  data-test="reference-data-loading-text">Loading&hellip;</span>
              <mode-report-data-table
                ng-if="(ctrl.referenceData$ | async:this).length"
                [references]="ctrl.referenceData$ | async:this"
              ></mode-report-data-table>
            </div>
          </div>

          <div class="next-well" ng-if="ctrl.canSeeHistory$">
            <report-details-schedule
              context-account="ctrl.contextAccount$ | async:this"
              account="ctrl.account$ | async:this"
              schedules-collection="ctrl.schedulesCollection$ | async:this"
              report="ctrl.report$ | async:this"
              report-run="ctrl.reportRun$ | async:this"
              can-create-schedule="ctrl.canEditReport$ | async:this"
            ></report-details-schedule>
          </div>

          <div class="next-well" ng-if="ctrl.dbtTables.length || (ctrl.reportDbtDataLoading$|async)">
            <div class="loading" ng-if="(ctrl.reportDbtDataLoading$|async)">
              <mode-animated-refresh-icon class="refresh-icon"></mode-animated-refresh-icon>
              <span class="loading-text">Loading...</span>
            </div>
            <dbt-details-table
              ng-if="ctrl.dbtTables.length"
              tables="ctrl.dbtTables"
              source-data-last-loaded-at="ctrl.dbtSourceLastLoadTime"
              status="ctrl.dbtStatus"
              message="ctrl.dbtMessage"
            ></dbt-details-table>
          </div>

          <div class="next-well" ng-if="ctrl.canSeeHistory$">
            <report-details-run-history
              report-runs-collection="ctrl.reportRunsCollection$ | async:this"
              current-run-token="ctrl.currentRunToken$ | async:this"
            ></report-details-run-history>
          </div>
        </div>
      </div>
    </div>
  </div>`,
  controller: ReportViewDetailsController,
  controllerAs: 'ctrl',
};
